import { stringify } from 'querystring'

import { AnimatePresence } from 'framer-motion'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { encodeQueryParams, useQueryParams } from 'use-query-params'

import { Layout } from '@/components/Layout'
import { MobileBottomContainerFixed } from '@/components/MobileBottomContainerFixed'
import { MobileBottomMenu } from '@/components/MobileBottomMenu'
import { GeoFilterModal } from '@/components/Modal/Types/GeoFilter'
import { PrerenderedModal } from '@/components/PrerenderedModal'
import { BreadcrumbModal } from '@/components/PrerenderedModal/Types/BreadcrumbModal'
import PrerenderedSeoLinks from '@/components/PrerenderedSeoLinks'
import SaveSearchFloatingBanner from '@/components/SaveSearchFloatingBanner'
import { CateCity, useCities } from '@/context/Cities'
import { CityPageContextProvider, useCityPage } from '@/context/CityPageContext'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { usePageYOffset } from '@/hooks/usePageYOffset'
import { useTagManager } from '@/hooks/useTagManager'
import {
  cleanOtherFilters,
  cleanPageParams,
  cleanPriorityFilter,
  cleanQueryParams,
  cleanSortingParams,
  PAGINATION_SIZE,
  queryParams,
  skipFilters,
} from '@/page-commons/listingsByFilter/helpers'
import { CanonicalFilter, FilterCity, FilterToRemove, FilterZone, GeoFilter, Page, QueryParams } from '@/types/filter'
import { Filter } from '@/types/graphql-generated-types'
import { SortBy, SortOptions } from '@/types/sort'
import { UserWithKratosId } from '@/utils/auth/auth-client-ssr'
import filtersMapper from '@/utils/filters-mapper'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { nikaMetaToLegacyMeta } from '@/utils/nika/listing-mapper'
import { sanitizeNull } from '@/utils/sanitize-null'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleShow } from '@/utils/tracking/eventsHandlers'

import { cityPageWrapper, contentPanel } from './styles.css'
import { useListings } from './useListings'

export type Source = 'listing' | 'priority'

const ListingContent = dynamic(() => import('@/containers/ListingContent'), {
  ssr: false,
})

const ListingMap = dynamic(() => import('@/containers/ListingMap'), {
  ssr: false,
})

const CityPageHeaderMobile = dynamic(() => import('@/components/CityPageHeaderMobile'), {
  ssr: false,
})

const getCleanedQueryParams = (
  zone: string,
  query,
  city: string,
  canonicalUrlFilter: CanonicalFilter
): Partial<QueryParams> => {
  const cleanedQueryParams: Partial<QueryParams> = cleanQueryParams(query)
  if (zone) {
    cleanedQueryParams.byZone = cleanedQueryParams.byZone ? [...cleanedQueryParams.byZone, zone] : [zone]
  }
  if (city) {
    cleanedQueryParams.byZone =
      cleanedQueryParams.byZone && cleanedQueryParams.byZone.length > 0 ? cleanedQueryParams.byZone : [city]
  }
  if (canonicalUrlFilter) {
    Object.keys(canonicalUrlFilter.filter).forEach((key) => {
      cleanedQueryParams[key] = canonicalUrlFilter.filter[key]
    })
  }
  return cleanedQueryParams
}

type Props = {
  canonicalFilter: CanonicalFilter | undefined
  city?: string
  geoSlugs?: string[]
  user: UserWithKratosId | null
  zoneSlugSegment?: string // it's something like `ponte-lambro-santa-giulia` or `3eme-arrondissement`
}

const ListingsByCityPageChild: React.FC<Props> = ({ canonicalFilter, city, geoSlugs, user, zoneSlugSegment }) => {
  const { lang } = useTranslation()
  const router = useRouter()
  const cities = useCities()
  const {
    filters: { setHasJustAppliedFilters },
    mapState: { isMapFullPage },
    saveSearchButtonState,
  } = useCityPage()
  const device = useDevice()
  const { closeModal: closeZoneModal, openModal: openZoneModal } = useModal()

  const [selectedPropertyId, setSelectedPropertyId] = useState(null)

  const zones = useMemo(() => (cities.router.city || cities.router.area).city_zones || [], [cities])
  const geoFilters: GeoFilter[] = useMemo(
    () => [
      ...zones.map((item): FilterZone => ({ ...item, kind: 'zone' })),
      ...cities.list.map(
        (cateCity: CateCity): FilterCity => ({
          kind: 'city',
          label: cateCity.translatedCityName,
          referenceAreaSlug: cateCity.referenceArea,
          slug: cateCity.slug,
        })
      ),
    ],
    [zones, cities]
  )

  const componentMount = useRef(false)

  const [query, setQuery] = useQueryParams(queryParams)

  const cleanedQueryParams = getCleanedQueryParams(zoneSlugSegment, query, city, canonicalFilter)

  const DEFAULT_SORTING = SortBy.Ranking

  const [priorityFilters, setPriorityFilters] = useState<Filter>(cleanPriorityFilter(cleanedQueryParams))
  const [otherFilters, setOtherFilters] = useState<Filter>(cleanOtherFilters(cleanedQueryParams))
  const [allFilters, setAllFilters] = useState<Filter>({
    ...cleanOtherFilters(cleanedQueryParams),
    ...cleanPriorityFilter(cleanedQueryParams),
  })
  const [sort, setSort] = useState<SortOptions>(cleanSortingParams(cleanedQueryParams, DEFAULT_SORTING))
  const [page, setPage] = useState<Page>(cleanPageParams(cleanedQueryParams))
  const [priorityPage, setPriorityPage] = useState<Page>(1)

  // TODO useZoneFilters should use NIKA instead of CATE-HASURA
  // const listingCountByZone = useZoneFilters(allFilters)
  const listingCountByZone = null

  const queryFilter = {
    byGeoSlug: geoSlugs,
    ...filtersMapper.formatFiltersForQuery(priorityFilters, geoFilters),
    ...otherFilters,
  }

  const {
    data: listingsData,
    isFetching,
    isLoading,
  } = useListings(
    sort,
    queryFilter,
    { page, page_size: PAGINATION_SIZE },
    { page: priorityPage, page_size: PAGINATION_SIZE }
  )

  const appliedGeoFilters: GeoFilter[] = useMemo(() => {
    const appliedGeoFiltersSlugs = filtersMapper.geoFilterToSlug(
      priorityFilters.byZone,
      geoFilters.map((gF) => gF.slug)
    )

    return geoFilters.filter((gF) => appliedGeoFiltersSlugs.includes(gF.slug))
  }, [priorityFilters, geoFilters])

  useEffect(() => {
    if (componentMount.current) {
      const newCleanedQueryParams = getCleanedQueryParams(zoneSlugSegment, query, city, canonicalFilter)
      const priorityFiltersParams = cleanPriorityFilter(newCleanedQueryParams)
      const otherFiltersParams = cleanOtherFilters(newCleanedQueryParams)
      const sortingParams: SortOptions = cleanSortingParams(newCleanedQueryParams, DEFAULT_SORTING)
      const pageParams: Page = cleanPageParams(newCleanedQueryParams)

      setPriorityFilters(priorityFiltersParams)
      setOtherFilters(otherFiltersParams)
      setAllFilters({ ...priorityFiltersParams, ...otherFiltersParams })
      setSort(sortingParams)
      // setSortPriority(sortingParams)
      setPage(pageParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  useEffect(() => {
    componentMount.current = true
  }, [])

  useEffect(() => {
    if (listingsData?.priorities) {
      handleShow(
        GTMEventCategory.ListingsPage,
        'PriorityAvailability-' + listingsData.priorities.potential_property_aggregate.aggregate.count
      )
    }
  }, [listingsData])

  const goToPathWithoutCanonicalFilter = (sanitizedQueryParams) => {
    const asPath = router.asPath
    const cityRouter = asPath.split('?')[0].split('/').slice(0, 3).join('/')

    const formattedQueryParams = stringify(encodeQueryParams(queryParams, sanitizedQueryParams))
    router.push(
      `${cityRouter}${formattedQueryParams && '?'}${new URLSearchParams(formattedQueryParams).toString()}`,
      null,
      { shallow: true }
    )
  }

  const sanitizedFiltersWithOrWithoutZone = (sanitizedFilters) => {
    return {
      ...sanitizedFilters,
      byZone: query.byZone ? priorityFilters.byZone : [],
    }
  }

  const updateFilters = (sanitizedFilters) => {
    const isCanonicalFilterRemoved =
      canonicalFilter?.filter &&
      Object.keys(canonicalFilter.filter).some((key) => !isEqual(canonicalFilter.filter[key], sanitizedFilters[key]))
    if (isCanonicalFilterRemoved) {
      goToPathWithoutCanonicalFilter(sanitizedFiltersWithOrWithoutZone(sanitizedFilters))
    } else {
      setQuery(sanitizedFiltersWithOrWithoutZone(sanitizedFilters), 'push')
    }
  }

  const onAddFilter = (addedFilter: Filter) => {
    const sanitizedFilters = sanitizeNull({ ...query, ...allFilters, ...addedFilter, pag: undefined })
    updateFilters(sanitizedFilters)
  }

  const onRemoveFilter = (filter: FilterToRemove) => {
    const sanitizedFilters = sanitizeNull({ ...omit(priorityFilters, filter.name), ...otherFilters, pag: undefined })
    updateFilters(sanitizedFilters)
  }

  const onChangeOtherFilters = (updatedOtherFilters: Filter) => {
    setHasJustAppliedFilters(true)
    const sanitizedFilters = sanitizeNull({
      sorting: query.sorting,
      ...priorityFilters,
      ...updatedOtherFilters,
      pag: undefined,
    })
    updateFilters(sanitizedFilters)
  }

  const onRemoveAllFilters = () => {
    setHasJustAppliedFilters(false)
    const newFilters = Object.keys(priorityFilters).reduce((obj, key: keyof Filter) => {
      if (skipFilters.includes(key)) obj[key] = priorityFilters[key]
      return obj
    }, {})
    const sanitizedFilters = sanitizeNull({ ...newFilters, pag: undefined })
    updateFilters(sanitizedFilters)
  }

  const onChangeSort = (sortParam: SortOptions) => {
    setQuery(sanitizeNull({ ...query, pag: undefined, sorting: sortParam }), 'push')
  }

  const onChangePage = (source: Source, pageParam: Page) => {
    if (source === 'priority') {
      setPriorityPage(pageParam)
      return
    }
    setQuery(changePageUrl(pageParam))
  }

  const changePageUrl = (pageParam: Page) => {
    return sanitizeNull({ ...query, pag: pageParam })
  }

  const changePageUrlString: (pageParam: Page) => string = (pageParam) => {
    const encodedQuery = encodeQueryParams(queryParams, changePageUrl(pageParam))
    return `?${stringify(encodedQuery)}`
  }

  const handleOpenZoneFilter = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    openZoneModal(
      <GeoFilterModal
        canonicalFilter={canonicalFilter}
        closeModal={() => {
          closeZoneModal()
        }}
        initialFilters={appliedGeoFilters}
        listingCountByZone={listingCountByZone}
      />,
      {
        desktopHeight: 590,
        desktopWidth: 640,
        overlay: true,
      }
    )
  }

  const listingsLength = listingsData?.listings?.meta.total_objects
  const hasListings = listingsLength > 0

  const [isTopListingsInView, setIsTopListingsInView] = useState(false)

  usePageYOffset((scrollY) => {
    const firstListingThreshold = 220
    const secondListingThreshold = 320
    const listingThreshold =
      listingsData?.listings?.meta.total_objects === 1 ? firstListingThreshold : secondListingThreshold
    if (scrollY > listingThreshold) {
      setIsTopListingsInView(true)
    }
  })

  const [hideSaveSearchAfterSearchSaved, setHideSaveSearchAfterSearchSaved] = useState(false)
  const noResults = !isFetching && !hasListings
  const showSaveSearchBanner =
    device === 'mobile' && !isMapFullPage && !hideSaveSearchAfterSearchSaved && isTopListingsInView && !noResults

  useEffect(() => {
    if (saveSearchButtonState === 'saved') {
      const feedbackDismiss = setTimeout(() => {
        setHideSaveSearchAfterSearchSaved(true)
      }, 1500)

      return () => clearTimeout(feedbackDismiss)
    }

    if (saveSearchButtonState === 'default') {
      setHideSaveSearchAfterSearchSaved(false)
    }
  }, [saveSearchButtonState])

  useTagManager({
    city: (cities.router.city || cities.router.area).name.toLowerCase(),
    language: lang,
    pageType: 'listingsByCity',
  })

  return (
    <>
      <PrerenderedModal>
        <BreadcrumbModal />
      </PrerenderedModal>

      <Layout contentWidth="full" showFooterOnMobile={false} showHeaderOnMobile={false}>
        <div className={cityPageWrapper}>
          <CityPageHeaderMobile
            appliedGeoFilters={appliedGeoFilters}
            filters={allFilters}
            geoSlugs={geoSlugs}
            handleOpenZoneFilter={handleOpenZoneFilter}
            onAddOtherFilters={onChangeOtherFilters}
            onRemoveAllFilters={onRemoveAllFilters}
            onRemoveFilter={onRemoveFilter}
          />

          {(isMapFullPage || device === 'desktop') && (
            <ListingMap filters={queryFilter} selectedPropertyId={selectedPropertyId} onAddFilter={onAddFilter} />
          )}

          {!isMapFullPage && (
            <div className={contentPanel}>
              <ListingContent
                appliedGeoFilters={appliedGeoFilters}
                changePageUrl={changePageUrlString}
                filters={allFilters}
                geoFilters={geoFilters}
                geoSlugs={queryFilter.byGeoSlug}
                handleOpenZoneFilter={handleOpenZoneFilter}
                isLoading={isLoading}
                listingsAndPriorities={listingsData}
                listingsMeta={nikaMetaToLegacyMeta(listingsData?.listings, page)}
                page={page}
                priorityPage={priorityPage}
                sort={sort}
                onAddFilter={onAddFilter}
                onAddOtherFilters={onChangeOtherFilters}
                onChangePage={onChangePage}
                onChangeSort={onChangeSort}
                onListingCardHover={null}
                onRemoveAllFilters={onRemoveAllFilters}
                onRemoveFilter={onRemoveFilter}
              />

              {isLocaleEnabledFor('prerenderedSeoLinks', lang) && (
                <PrerenderedSeoLinks canonicalFilter={canonicalFilter} isLoading={isFetching} />
              )}
            </div>
          )}
          <MobileBottomContainerFixed>
            <>
              <AnimatePresence>
                {showSaveSearchBanner &&
                  isLocaleEnabledFor('saveSearch', lang) &&
                  saveSearchButtonState === 'default' && (
                    <SaveSearchFloatingBanner
                      appliedGeoFilters={appliedGeoFilters}
                      filters={allFilters}
                      geoSlugs={geoSlugs}
                      hasOnlyOneListing={listingsLength === 1}
                    />
                  )}
              </AnimatePresence>
              {!isMapFullPage && <MobileBottomMenu activeButton="search" />}
            </>
          </MobileBottomContainerFixed>
        </div>
      </Layout>
    </>
  )
}

export const ListingsByCityPage: React.FC<Props> = (props) => {
  return (
    <CityPageContextProvider>
      <ListingsByCityPageChild {...props} />
    </CityPageContextProvider>
  )
}
